import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient } from '../lib/api-client';

interface AuthContextType {
  isAuthenticated: boolean;
  /** Sign in with a discoverable passkey. Throws on failure or user cancellation. */
  loginWithPasskey: () => Promise<void>;
  /** Register the first passkey with a one-time token and sign in. Throws on failure. */
  registerPasskeyWithToken: (token: string, name: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if JWT token is stored and valid
    const checkAuth = async () => {
      if (apiClient.isAuthenticated()) {
        try {
          await apiClient.getCurrentUser();
          setIsAuthenticated(true);
        } catch {
          apiClient.clearToken();
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    void checkAuth();
  }, []);

  const loginWithPasskey = async (): Promise<void> => {
    const { challengeId, options } = await apiClient.getPasskeyLoginOptions();
    const response = await startAuthentication({ optionsJSON: options });
    const result = await apiClient.verifyPasskeyLogin(challengeId, response);
    apiClient.setToken(result.token);
    setIsAuthenticated(true);
  };

  const registerPasskeyWithToken = async (token: string, name: string): Promise<void> => {
    const { challengeId, options } = await apiClient.getPasskeyTokenRegistrationOptions(token);
    const response = await startRegistration({ optionsJSON: options });
    const result = await apiClient.verifyPasskeyTokenRegistration({
      token,
      challengeId,
      response,
      name
    });
    apiClient.setToken(result.token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    apiClient.clearToken();
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, loginWithPasskey, registerPasskeyWithToken, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
